import { EventEmitter, Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from '../../environments/environment';
import { DUMMY_ASSIGNMENT_QUESTIONS_ONE, DUMMY_ASSIGNMENT_QUESTIONS_TWO } from '../config/dummy_assignment_questions';
import { DUMMY_ASSIGNMENTS } from '../config/dummy_assignments';
import { EndPoint } from '../config/endpoint';
import { DummyAssignmentId } from '../enum/assignment';
import { Assignment, AssignmentQuestion } from '../interface/assignment';
import { Grades } from '../interface/grades';
import { Subjects } from '../interface/subject';
import { HttpService } from './http.service';

// Const maps to associate dummy data with specific assignment IDs for trial users
export const DUMMY_ASSIGNMENT_MAP: Readonly<Record<DummyAssignmentId, Assignment>> = {
  [DummyAssignmentId.FIRST]: DUMMY_ASSIGNMENTS[0],
  [DummyAssignmentId.SECOND]: DUMMY_ASSIGNMENTS[1],
};

// Const maps to associate dummy data with specific assignment questions IDs for trial users
export const DUMMY_QUESTIONS_MAP: Readonly<Record<DummyAssignmentId, any[]>> = {
  [DummyAssignmentId.FIRST]: DUMMY_ASSIGNMENT_QUESTIONS_ONE,
  [DummyAssignmentId.SECOND]: DUMMY_ASSIGNMENT_QUESTIONS_TWO,
};
@Injectable({
  providedIn: 'root',
})
export class AssignmentsService {
  assignmentsData: any;
  subjects: Subjects[];
  grades: Grades[];
  public ifUserEditedAssignment = new EventEmitter<boolean>();
  public questionAddedByQuestionBank = new EventEmitter<boolean>();
  public isArchivedOptionChange = new EventEmitter<any>();

  constructor(private httpService: HttpService) {}

  async addAssignment(body: object) {
    try {
      const token = await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.Assignments}`, body),
      );
      return this.httpService.decodeTokenPagination(token);
    } catch (error) {
      throw error;
    }
  }

  async updateAssignment(id: number, body: object) {
    try {
      return await firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.Assignments}/${id}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async assignmentByUserAndSchoolId(queryParams: any) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.Assignments}`, {
          params: queryParams,
        }),
      );
      if (queryParams && !queryParams.page) {
        return this.httpService.decodeTokenPagination(response);
      } else {
        return this.httpService.decryptToken(response);
      }
    } catch (error) {
      throw error;
    }
  }

  async assignmentByClassroomAndSubjectId(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.assignment_by_classroom_subject_id}`, {
          params: queryParams,
        }),
      );
      return this.httpService.decodeUserToken(response);
    } catch (error) {
      throw error;
    }
  }

  async assignmentByClassroomId(queryParams: object) {
    try {
      return await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.assignments_by_classroom_id}`, {
          params: queryParams,
        }),
      );
    } catch (error) {
      throw error;
    }
  }

  async AssignmentsByUserIdAndTime(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.assignments_by_user_id_and_time}`,
          queryParams,
        ),
      );
      return this.httpService.decodeUserAssignmentToken(response);
    } catch (error) {
      throw error;
    }
  }

  async studentAssignmentsByAssignmentIdAnduserId(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.student_assignment_by_assignments_id_user_id}`,
          queryParams,
        ),
      );
      return this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }

  async AssignmentQuestionsByAssignmentsIds(queryParams: object) {
    try {
      const assignmentToken = await firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.assignment_questions_by_assignements_id}`,
          queryParams,
        ),
      );
      return this.httpService.decodeToken(assignmentToken);
    } catch (error) {
      throw error;
    }
  }

  async assignmentById(id: number) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.Assignments}/${id}`),
      );
      return this.httpService.decodeTokenPagination(response);
    } catch (error) {
      throw error;
    }
  }

  async deleteAssignment(id: number) {
    try {
      return await firstValueFrom(
        this.httpService.delete<any>(`${environment.XANO_SERVER_URL}${EndPoint.Assignments}/${id}`),
      );
    } catch (error) {
      throw error;
    }
  }
  async deleteBulkQuestionByAssignmentId(body) {
    try {
      return await firstValueFrom(
        this.httpService.delete<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.delete_bulk_questions_by_assignment_id}`,
          {
            params: body,
          },
        ),
      );
    } catch (error) {
      throw error;
    }
  }

  async teachersBySchoolId(queryParams: object) {
    try {
      const teachers = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.TeachersBySchoolId}`, {
          params: queryParams,
        }),
      );
      return this.httpService.decodeToken(teachers);
    } catch (error) {
      throw error;
    }
  }
  async teacherAssignments(queryParams: object) {
    try {
      return await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.TeacherAssignments}`, {
          params: queryParams,
        }),
      );
    } catch (error) {
      throw error;
    }
  }

  async assignments(queryParams: object) {
    try {
      return await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.Assignments}`, {
          params: queryParams,
        }),
      );
    } catch (error) {
      throw error;
    }
  }

  getSubmittedAssignment(data) {
    try {
      return firstValueFrom(
        this.httpService.get<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.StudentSubmittedAssignment}/${data.user_id}/${data.assignment_id}`,
        ),
      );
    } catch (error) {
      throw error;
    }
  }

  async checkServerAssignment(queryParams: object) {
    try {
      return await firstValueFrom(
        this.httpService.get<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.student_submitted_assignment_by_user_id_and_assignment_id}`,
          {
            params: queryParams,
          },
        ),
      );
    } catch (error) {
      throw error;
    }
  }

  submitAssignment(body) {
    try {
      return firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.StudentSubmittedAssignment}`, body),
      );
    } catch (error) {
      throw error;
    }
  }
  updateStudentAssignment(id, body) {
    try {
      return firstValueFrom(
        this.httpService.post<any>(`${environment.XANO_SERVER_URL}${EndPoint.StudentSubmittedAssignment}/${id}`, body),
      );
    } catch (error) {
      throw error;
    }
  }

  async previousAssignmentBySubjectId(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_all_previous_year_assignment}`, {
          params: queryParams,
        }),
      );
      return response;
      // return this.httpService.decodeUserToken(response);
    } catch (error) {
      throw error;
    }
  }
  async addMultipleAssignmentQuestion(body) {
    try {
      const multipleMessageToken = await firstValueFrom(
        this.httpService.post(`${environment.XANO_SERVER_URL}${EndPoint.add_multiple_assignment_question}`, body),
      );

      return multipleMessageToken;
    } catch (error) {
      throw error;
    }
  }

  async previousAssignmentsByUserIdAndTime(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_previous_assignments_for_student}`, {
          params: queryParams,
        }),
      );
      return this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getSavedAnswerOfStudentAsssignments(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.get_saved_answer_of_student_assignment}`, {
          params: queryParams,
        }),
      );
      return response;
    } catch (error) {
      console.log(
        '🚀 ~ assignments.service.ts:266 ~ AssignmentsService ~ getSavedAnswerOfStudentAsssignments ~ error:',
        error,
      );
    }
  }

  async getStudentSubmittedAssignment(queryParams: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.get<any>(`${environment.XANO_SERVER_URL}${EndPoint.student_assignment_by_assignment_id}`, {
          params: queryParams,
        }),
      );
      return this.httpService.decodeToken(response);
    } catch (error) {
      throw error;
    }
  }

  async getAssignmentQuestionsForQuestionBank(body: object) {
    try {
      const response = await firstValueFrom(
        this.httpService.post<any>(
          `${environment.XANO_SERVER_URL}${EndPoint.get_assignment_questions_for_question_bank}`,
          body,
        ),
      );
      return response;
      // return this.httpService.decryptToken(response);
    } catch (error) {
      throw error;
    }
  }

  /**
   * @description Utility function to fetch assignment details by ID, with support for trial users using dummy data.
   * @param id
   * @param isTrialUser
   * @param assignmentService
   * @returns List of assignment details for real or trial users
   */
  async fetchAssignmentsWithTrialUser(id: number, isTrialUser: boolean): Promise<any> {
    if (isTrialUser && id in DUMMY_ASSIGNMENT_MAP) {
      return Promise.resolve(DUMMY_ASSIGNMENT_MAP[id as DummyAssignmentId]);
    }

    return await this.assignmentById(id);
  }

  /**
   * @description Utility function to fetch assignment questions, with support for trial users using dummy data based on assignment ID.
   * @param assignmentId
   * @param isTrialUser
   * @param getQuestionsByUserId
   * @returns List of assignment questions for real or trial users
   */
  async fetchAssignmentQuestionsWithTrialUser(
    assignmentId: number,
    isTrialUser: boolean,
    getQuestionsByUserId: () => Promise<any[]>,
  ): Promise<AssignmentQuestion[]> {
    const userQuestions = await getQuestionsByUserId();

    if (isTrialUser && assignmentId in DUMMY_QUESTIONS_MAP) {
      return [...DUMMY_QUESTIONS_MAP[assignmentId as DummyAssignmentId], ...userQuestions];
    }

    return userQuestions;
  }

  /**
   * @description Utility function to delete an assignment and assignment questions by ID
   * @param id
   * @returns Promise resolving to the result of the deletion operation

   */
  async deleteAssignmentbyId(id: number): Promise<any> {
    try {
      return await firstValueFrom(
        this.httpService.delete<any>(`${environment.XANO_SERVER_URL}${EndPoint.delete_homework_assignment}/${id}`),
      );
    } catch (error) {
      throw error;
    }
  }
}
